/**
 * DEC-24 item 4: reading the ratio the service sends.
 *
 * The arithmetic is here, apart from the page, so it can be asked directly
 * rather than only through a render. The one thing it must never do is invent
 * a number: a share of nothing is ABSENT, not zero, which is the rule the
 * Overview's refusal rate already follows. "Nothing was decided in this hour"
 * and "everything was approved" are two facts and 0% says the second.
 */

export interface RatioRow {
  hour: string
  lane: string
  total: number
  we_approved: number
  we_refused: number
  authority_approved: number
  approved_then_refused: number
  refusals_by_basis?: Record<string, number>
}

export interface ComparisonRow {
  hour_of_day: number
  lane: string
  days: number
  total: number
  we_approved: number
  we_refused: number
  authority_approved: number
  approved_then_refused: number
}

export interface RatioAnswer {
  windows: (
    | { window: 'now'; rows: RatioRow[] }
    | { window: 'previous'; days: number; rows: ComparisonRow[] }
  )[]
  hours: number
  comparison_days: number
  approved_then_refused_bases: string[]
}

/** The share an authority approved that this service then refused.
 *
 *  null when the authority approved nothing in the bucket, because a share of
 *  nothing is not zero. An hour with no approvals is an idle hour, and
 *  rendering it as 0% says the lane was perfect.
 */
export function refusedShare(row: {
  authority_approved: number
  approved_then_refused: number
}): number | null {
  if (!row.authority_approved) return null
  return row.approved_then_refused / row.authority_approved
}

/** The same hour on the previous days, as an average, with the days behind it.
 *
 *  null rather than 0 when no earlier day carried this hour: a new deployment
 *  has no history and "0 on average" would read as a quiet week.
 */
export function perDay(row: { days: number; total: number }): number | null {
  if (!row.days) return null
  return row.total / row.days
}

/** Today's buckets paired with the same hour of day from the comparison.
 *
 *  Pairing on the HOUR OF DAY and the lane, which is what makes a comparison
 *  a comparison. Pairing on position would line up 09:00 against whatever
 *  happened to be the first earlier bucket, and the two would drift apart as
 *  soon as one hour had no decisions at all.
 */
export function pairWithHistory(
  now: RatioRow[],
  previous: ComparisonRow[],
): { row: RatioRow; history: ComparisonRow | null }[] {
  const byKey = new Map<string, ComparisonRow>()
  for (const p of previous) byKey.set(`${p.hour_of_day}:${p.lane}`, p)
  return now.map((row) => ({
    row,
    history:
      byKey.get(`${new Date(row.hour).getUTCHours()}:${row.lane}`) ?? null,
  }))
}
